import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { useNotificationSSE } from '@/hooks/useNotificationSSE';
import {
  deleteNotification,
  getNotificationCount,
  getNotifications,
  markAllNotificationsRead,
  markNotificationRead,
} from '@/lib/api/notificationsApi';
import { queryKeys } from '@/lib/query/queryKeys';
import { useAuthStore } from '@/stores/auth-store';
import { NotificationType } from '@shared/enums/domain.enum';
import type { AuthUser, Notification } from '@shared/types/entities';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  AlertTriangle,
  Bell,
  Building2,
  CheckCircle2,
  CreditCard,
  RefreshCw,
  ShieldCheck,
  ShieldX,
  Trash2,
  Trophy,
  UserCheck,
  UserPlus,
  UserX,
  Users,
  XCircle,
} from 'lucide-react';

// ─── Helpers ─────────────────────────────────────────────────────────────────

function timeAgo(dateStr: string): string {
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60_000);
  if (mins < 1) return "à l'instant";
  if (mins < 60) return `il y a ${mins} min`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `il y a ${hours}h`;
  return `il y a ${Math.floor(hours / 24)}j`;
}

type LucideIcon = React.ComponentType<{ className?: string }>;

const TYPE_ICON: Record<NotificationType, LucideIcon> = {
  [NotificationType.TEAM_REGISTERED]: Users,
  [NotificationType.TEAM_CONFIRMED]: CheckCircle2,
  [NotificationType.TEAM_REJECTED]: XCircle,
  [NotificationType.PAYMENT_CONFIRMED]: CreditCard,
  [NotificationType.PAYMENT_REFUNDED]: RefreshCw,
  [NotificationType.PAYMENT_PARTIALLY_REFUNDED]: RefreshCw,
  [NotificationType.COMPETITION_RESULTS]: Trophy,
  [NotificationType.MEMBER_REQUEST]: UserPlus,
  [NotificationType.MEMBER_APPROVED]: UserCheck,
  [NotificationType.MEMBER_REJECTED]: UserX,
  [NotificationType.AFFILIATION_REQUEST]: Building2,
  [NotificationType.AFFILIATION_APPROVED]: Building2,
  [NotificationType.CLAIM_APPROVED]: ShieldCheck,
  [NotificationType.CLAIM_REJECTED]: ShieldX,
  [NotificationType.REFUND_REQUEST_CREATED]: RefreshCw,
  [NotificationType.REFUND_APPROVED]: CheckCircle2,
  [NotificationType.REFUND_REJECTED]: XCircle,
  [NotificationType.SUBSCRIPTION_PAYMENT_FAILED]: AlertTriangle,
  [NotificationType.SUBSCRIPTION_CANCELLED]: XCircle,
  [NotificationType.PAYMENT_DISPUTED]: AlertTriangle,
  [NotificationType.COMPETITION_REMINDER_J7]: Bell,
  [NotificationType.COMPETITION_REMINDER_J1]: Bell,
  [NotificationType.ORG_ADMIN_REQUEST]: UserPlus,
  [NotificationType.ORG_ADMIN_REQUEST_ACCEPTED]: ShieldCheck,
  [NotificationType.ORG_ADMIN_REQUEST_REJECTED]: ShieldX,
};

const TYPE_ICON_COLOR: Record<NotificationType, string> = {
  [NotificationType.TEAM_REGISTERED]: 'text-blue-500',
  [NotificationType.TEAM_CONFIRMED]: 'text-emerald-500',
  [NotificationType.TEAM_REJECTED]: 'text-red-500',
  [NotificationType.PAYMENT_CONFIRMED]: 'text-emerald-500',
  [NotificationType.PAYMENT_REFUNDED]: 'text-amber-500',
  [NotificationType.PAYMENT_PARTIALLY_REFUNDED]: 'text-amber-500',
  [NotificationType.COMPETITION_RESULTS]: 'text-amber-500',
  [NotificationType.MEMBER_REQUEST]: 'text-blue-500',
  [NotificationType.MEMBER_APPROVED]: 'text-emerald-500',
  [NotificationType.MEMBER_REJECTED]: 'text-red-500',
  [NotificationType.AFFILIATION_REQUEST]: 'text-purple-500',
  [NotificationType.AFFILIATION_APPROVED]: 'text-purple-500',
  [NotificationType.CLAIM_APPROVED]: 'text-emerald-500',
  [NotificationType.CLAIM_REJECTED]: 'text-red-500',
  [NotificationType.REFUND_REQUEST_CREATED]: 'text-amber-500',
  [NotificationType.REFUND_APPROVED]: 'text-emerald-500',
  [NotificationType.REFUND_REJECTED]: 'text-red-500',
  [NotificationType.SUBSCRIPTION_PAYMENT_FAILED]: 'text-red-500',
  [NotificationType.SUBSCRIPTION_CANCELLED]: 'text-slate-500',
  [NotificationType.PAYMENT_DISPUTED]: 'text-red-600',
  [NotificationType.COMPETITION_REMINDER_J7]: 'text-blue-400',
  [NotificationType.COMPETITION_REMINDER_J1]: 'text-blue-400',
  [NotificationType.ORG_ADMIN_REQUEST]: 'text-indigo-500',
  [NotificationType.ORG_ADMIN_REQUEST_ACCEPTED]: 'text-emerald-500',
  [NotificationType.ORG_ADMIN_REQUEST_REJECTED]: 'text-red-500',
};

function getTargetRoute(n: Notification, user: AuthUser | null): string | null {
  const organizationId = user?.organizationsAdmin[0]?.organizationId;
  switch (n.type) {
    case NotificationType.TEAM_REGISTERED:
      return n.competitionId && organizationId
        ? `/organizer/inscriptions?competitionId=${n.competitionId}`
        : '/player/inscriptions';
    case NotificationType.TEAM_CONFIRMED:
    case NotificationType.TEAM_REJECTED:
    case NotificationType.PAYMENT_CONFIRMED:
    case NotificationType.PAYMENT_REFUNDED:
    case NotificationType.REFUND_REQUEST_CREATED:
    case NotificationType.REFUND_APPROVED:
    case NotificationType.REFUND_REJECTED:
      return '/player/inscriptions';
    case NotificationType.COMPETITION_RESULTS:
      return n.competitionId ? `/competition/${n.competitionId}/results` : null;
    case NotificationType.MEMBER_REQUEST:
      return '/club/members';
    case NotificationType.MEMBER_APPROVED:
    case NotificationType.MEMBER_REJECTED:
      return '/club/dashboard';
    case NotificationType.AFFILIATION_REQUEST:
      return '/organizer/clubs';
    case NotificationType.AFFILIATION_APPROVED:
      return '/club/settings';
    case NotificationType.CLAIM_APPROVED:
    case NotificationType.CLAIM_REJECTED:
      return '/club/dashboard';
    case NotificationType.SUBSCRIPTION_PAYMENT_FAILED:
    case NotificationType.SUBSCRIPTION_CANCELLED:
      return '/organizer/settings?tab=subscription';
    case NotificationType.PAYMENT_DISPUTED:
      return null;
    case NotificationType.COMPETITION_REMINDER_J7:
    case NotificationType.COMPETITION_REMINDER_J1:
      return '/player/inscriptions';
    case NotificationType.ORG_ADMIN_REQUEST:
      return '/organizer/admin-requests';
    case NotificationType.ORG_ADMIN_REQUEST_ACCEPTED:
      return '/organizer/dashboard';
    case NotificationType.ORG_ADMIN_REQUEST_REJECTED:
      return '/player/dashboard';
    default:
      return null;
  }
}

// ─── Sub-components ───────────────────────────────────────────────────────────

function SkeletonItem() {
  return (
    <div className='flex items-start gap-3 px-4 py-3 animate-pulse'>
      <div className='w-8 h-8 bg-slate-200 rounded-full shrink-0' />
      <div className='flex-1 space-y-2'>
        <div className='h-3 bg-slate-200 rounded w-3/4' />
        <div className='h-3 bg-slate-200 rounded w-full' />
        <div className='h-2 bg-slate-100 rounded w-1/4' />
      </div>
    </div>
  );
}

interface NotificationItemProps {
  notification: Notification;
  onMarkRead: (id: number) => void;
  onDelete: (id: number) => void;
  onNavigate: (n: Notification) => void;
}

function NotificationItem({ notification: n, onMarkRead, onDelete, onNavigate }: NotificationItemProps) {
  const Icon = TYPE_ICON[n.type] ?? Bell;
  const iconColor = TYPE_ICON_COLOR[n.type] ?? 'text-slate-500';

  return (
    <div
      className={`flex items-start gap-3 px-4 py-3 cursor-pointer hover:bg-slate-50 transition-colors group ${
        !n.isRead ? 'bg-blue-50/40' : ''
      }`}
      onClick={() => onNavigate(n)}
      role='button'
      tabIndex={0}
      onKeyDown={e => {
        if (e.key === 'Enter' || e.key === ' ') onNavigate(n);
      }}
    >
      {/* Point non-lu */}
      <div className='relative shrink-0 mt-0.5'>
        <div className={`w-8 h-8 rounded-full flex items-center justify-center bg-slate-100`}>
          <Icon className={`w-4 h-4 ${iconColor}`} />
        </div>
        {!n.isRead && <span className='absolute -top-0.5 -right-0.5 w-2 h-2 bg-blue-500 rounded-full' />}
      </div>

      {/* Contenu */}
      <div className='flex-1 min-w-0'>
        <p
          className={`text-sm leading-snug ${!n.isRead ? 'font-semibold text-slate-800' : 'font-medium text-slate-700'}`}
        >
          {n.title}
        </p>
        <p className='text-xs text-slate-500 mt-0.5 line-clamp-2'>{n.body}</p>
        <p className='text-[10px] text-slate-400 mt-1'>{timeAgo(n.createdAt)}</p>
      </div>

      {/* Actions (visibles au hover) */}
      <div
        className='flex flex-col gap-1 opacity-0 group-hover:opacity-100 transition-opacity shrink-0'
        onClick={e => e.stopPropagation()}
      >
        {!n.isRead && (
          <button
            type='button'
            title='Marquer comme lu'
            onClick={() => onMarkRead(n.id)}
            className='p-1 rounded hover:bg-slate-200 text-slate-400 hover:text-emerald-600 transition-colors'
          >
            <CheckCircle2 className='w-3.5 h-3.5' />
          </button>
        )}
        <button
          type='button'
          title='Supprimer'
          onClick={() => onDelete(n.id)}
          className='p-1 rounded hover:bg-slate-200 text-slate-400 hover:text-red-500 transition-colors'
        >
          <Trash2 className='w-3.5 h-3.5' />
        </button>
      </div>
    </div>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────

export default function NotificationBell() {
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement | undefined>(undefined);
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const isAuthenticated = useAuthStore(s => s.isAuthenticated);
  const user = useAuthStore(s => s.user);

  const { fallbackPolling } = useNotificationSSE();

  // ── Count query (poll 30s) ──
  const { data: countData } = useQuery({
    queryKey: queryKeys.notifications.count(),
    queryFn: getNotificationCount,
    refetchInterval: fallbackPolling ? 30_000 : false,
    refetchOnWindowFocus: true,
    enabled: isAuthenticated,
  });

  const unreadCount = countData?.unread ?? 0;

  // ── List query (lazy — uniquement quand le dropdown est ouvert) ──
  const { data: listData, isLoading: listLoading } = useQuery({
    queryKey: queryKeys.notifications.list(),
    queryFn: () => getNotifications({ limit: 20 }),
    enabled: isAuthenticated && open,
  });

  const notifications = listData?.data ?? [];

  // ── Mutations ──
  const invalidateNotifications = () => {
    void queryClient.invalidateQueries({ queryKey: queryKeys.notifications.count() });
    void queryClient.invalidateQueries({ queryKey: queryKeys.notifications.list() });
  };

  const markReadMutation = useMutation({
    mutationFn: markNotificationRead,
    onSuccess: invalidateNotifications,
  });

  const markAllReadMutation = useMutation({
    mutationFn: markAllNotificationsRead,
    onSuccess: invalidateNotifications,
  });

  const deleteMutation = useMutation({
    mutationFn: deleteNotification,
    onSuccess: invalidateNotifications,
  });

  // ── Fermeture au clic extérieur ──
  useEffect(() => {
    if (!open) return;

    function handleMouseDown(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }

    document.addEventListener('mousedown', handleMouseDown);
    return () => document.removeEventListener('mousedown', handleMouseDown);
  }, [open]);

  // ── Navigation depuis un item ──
  function handleNavigate(n: Notification) {
    if (!n.isRead) {
      markReadMutation.mutate(n.id);
    }
    const route = getTargetRoute(n, user);
    if (route) {
      navigate(route);
    }
    setOpen(false);
  }

  return (
    <div
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      ref={containerRef as any}
      className='relative'
    >
      {/* Bouton cloche */}
      <button
        type='button'
        onClick={() => setOpen(prev => !prev)}
        className='relative p-2 rounded-lg hover:bg-slate-700 transition-colors cursor-pointer'
        aria-label='Notifications'
        title={'Voir mes notifications'}
      >
        <Bell className='w-5 h-5 text-slate-300' />
        {unreadCount > 0 && (
          <span className='absolute -top-0.5 -right-0.5 min-w-4.5 h-4.5 bg-red-500 text-white text-[10px] font-black rounded-full flex items-center justify-center px-1'>
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>

      {/* Dropdown */}
      {open && (
        <div className='absolute -right-16 top-full mt-2 w-70 max-w-70 max-h-120 mt-5 bg-white rounded-2xl shadow-2xl border border-slate-200 z-50 overflow-hidden flex flex-col'>
          {/* Header */}
          <div className='flex items-center justify-between px-4 py-3 border-b border-slate-100 shrink-0'>
            <div className='flex items-center gap-2'>
              <span className='text-sm font-bold text-slate-800'>Notifications</span>
              {unreadCount > 0 && (
                <span className='bg-blue-100 text-blue-700 text-xs font-semibold px-2 py-0.5 rounded-full'>
                  {unreadCount}
                </span>
              )}
            </div>
            {unreadCount > 0 && (
              <button
                type='button'
                onClick={() => markAllReadMutation.mutate()}
                disabled={markAllReadMutation.isPending}
                className='text-xs text-blue-600 hover:text-blue-800 font-medium transition-colors disabled:opacity-50'
              >
                Tout marquer lu
              </button>
            )}
          </div>

          {/* Body */}
          <div className='overflow-y-auto flex-1'>
            {listLoading ? (
              <>
                <SkeletonItem />
                <SkeletonItem />
                <SkeletonItem />
              </>
            ) : notifications.length === 0 ? (
              <div className='flex flex-col items-center justify-center py-12 text-slate-400 gap-3'>
                <Bell className='w-8 h-8 opacity-40' />
                <p className='text-sm'>Aucune notification</p>
              </div>
            ) : (
              <div className='divide-y divide-slate-100'>
                {notifications.map(n => (
                  <NotificationItem
                    key={n.id}
                    notification={n}
                    onMarkRead={id => markReadMutation.mutate(id)}
                    onDelete={id => deleteMutation.mutate(id)}
                    onNavigate={handleNavigate}
                  />
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
